import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, Egg, PawPrint, Users, Send, Images, Baby, CalendarClock, Share2, FileText } from "lucide-react";
import { WaitlistMessageDialog } from "./WaitlistMessageDialog";
import { LitterUpdatesManager } from "./LitterUpdatesManager";
import { BabiesManager } from "./BabiesManager";
import { SlotsManager } from "./SlotsManager";
import { LitterShareDialog } from "../LitterShareDialog";
import { DueDateEditor } from "./DueDateEditor";
import { Input } from "../ui/input";
import { api, errText, API } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { statusLabel, STATUS_CLS, fmtDay, kindLabel, speciesLabel } from "../../lib/litters";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { LitterFormDialog } from "./LitterFormDialog";
import { Empty, Loader } from "./shared";
import { JourneyShareButton } from "../journey/JourneyShareButton";

const WaitlistTable = ({ litter, onChanged }) => {
  const { t } = useLanguage();
  const patch = async (e, body) => {
    try { await api.patch(`/my/litters/${litter.id}/waitlist/${e.id}`, body); onChanged(); } catch (err) { toast.error(errText(err)); }
  };
  const mark = (e, contacted) => patch(e, { contacted });
  const remove = async (e) => {
    try { await api.delete(`/my/litters/${litter.id}/waitlist/${e.id}`); onChanged(); } catch (err) { toast.error(errText(err)); }
  };
  if (!litter.waitlist.length) return <p className="text-xs text-slate-400 mt-3">{t("bl_none")}</p>;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm" data-testid={`litter-waitlist-${litter.id}`}>
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
          <th className="py-1.5 pr-2">#</th><th className="py-1.5 pr-2">{t("bl_member")}</th><th className="py-1.5 pr-2">{t("bl_pref_note")}</th><th className="py-1.5 pr-2">{t("bl_contacted")}</th><th className="py-1.5 pr-2">{t("wr_reserved")}</th><th className="py-1.5 pr-2">{t("rv_went_home")}</th><th />
        </tr></thead>
        <tbody>
          {litter.waitlist.map((e, i) => (
            <tr key={e.id} className="border-b border-slate-50" data-testid={`waitlist-entry-${e.id}`}>
              <td className="py-2 pr-2 font-bold text-[#12234A]">{i + 1}</td>
              <td className="py-2 pr-2"><p className="font-medium text-[#12234A]">{e.name}</p><a href={`mailto:${e.email}`} className="text-[11px] text-[#F5551E] hover:underline">{e.email}</a></td>
              <td className="py-2 pr-2 text-xs text-slate-600 max-w-[220px]">{e.preference && <span className="font-semibold">{e.preference}</span>}{e.preference && e.note ? " · " : ""}{e.note}</td>
              <td className="py-2 pr-2"><Checkbox checked={!!e.contacted} onCheckedChange={(v) => mark(e, !!v)} data-testid={`waitlist-contacted-${e.id}`} /></td>
              <td className="py-2 pr-2 min-w-[190px]">
                <div className="flex items-center gap-2">
                  <Checkbox checked={!!e.reserved} onCheckedChange={(v) => patch(e, { reserved: !!v })} data-testid={`waitlist-reserved-${e.id}`} />
                  {e.reserved && <Input defaultValue={e.deposit_note || ""} maxLength={200} placeholder={t("wr_deposit_ph")} data-testid={`waitlist-deposit-${e.id}`}
                    onBlur={(ev) => ev.target.value !== (e.deposit_note || "") && patch(e, { deposit_note: ev.target.value })} className="h-7 rounded-lg text-[11px]" />}
                </div>
              </td>
              <td className="py-2 pr-2"><Checkbox checked={!!e.went_home} onCheckedChange={(v) => patch(e, { went_home: !!v })} data-testid={`waitlist-went-home-${e.id}`} /></td>
              <td className="py-2 text-right">
                <AlertDialog>
                  <AlertDialogTrigger asChild><button data-testid={`waitlist-remove-${e.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader><AlertDialogTitle>{e.name} — {t("bl_rm_title")}</AlertDialogTitle><AlertDialogDescription>{t("bl_rm_desc")}</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>{t("lt_cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => remove(e)} data-testid={`waitlist-remove-confirm-${e.id}`} className="bg-red-500 hover:bg-red-600">{t("bl_remove")}</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LitterCard = ({ l, onEdit, onReload, onMessage, onUpdates, onBabies, onSlots, onShare }) => {
  const { t, lang } = useLanguage();
  const exportCsv = async () => {
    try {
      const res = await api.get(`/my/litters/${l.id}/waitlist.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `waitlist-${l.breed.toLowerCase().replace(/\s+/g, "-")}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errText(e)); }
  };
  const del = async () => {
    try { await api.delete(`/my/litters/${l.id}`); toast.success(t("bl_deleted")); onReload(); } catch (e) { toast.error(errText(e)); }
  };
  const Icon = l.kind === "clutch" ? Egg : PawPrint;
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-4" data-testid={`my-litter-${l.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="h-10 w-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center shrink-0"><Icon size={18} /></span>
          <div className="min-w-0">
            <p className="font-semibold text-[#12234A]">{l.title || `${l.breed} · ${kindLabel(l.kind, t)}`}</p>
            <p className="text-xs text-slate-500 mt-0.5">{kindLabel(l.kind, t)} · {speciesLabel(l.species, lang)} · {l.breed}{l.expected_date ? ` · ${fmtDay(l.expected_date, lang)}` : ""}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[l.shown_status || l.status]}`}>{statusLabel(l.shown_status || l.status, l.kind, t)}</span>
              <span className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Users size={12} /> {l.waitlist_count}{l.waitlist_cap ? ` / ${l.waitlist_cap}` : ""} {t("bl_on_wl")}{!l.waitlist_open ? ` · ${t("bl_closed")}` : ""}</span>
              {l.status !== "closed" && <DueDateEditor litter={l} lang={lang} onSaved={onReload} />}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onMessage(l)} disabled={!l.waitlist_count} data-testid={`litter-message-${l.id}`} className="rounded-lg h-8 gap-1 border-purple-200 text-purple-700 hover:bg-purple-50"><Send size={13} /> {t("wm_btn")}</Button>
          <Button size="sm" variant="outline" onClick={() => onSlots(l)} data-testid={`litter-slots-${l.id}`} className="rounded-lg h-8 gap-1"><CalendarClock size={13} /> {t("sl_btn")}</Button>
          <Button size="sm" variant="outline" onClick={() => onShare(l)} data-testid={`litter-share-${l.id}`} className="rounded-lg h-8 gap-1"><Share2 size={13} /> {t("sh_btn")}</Button>
          <Button size="sm" variant="outline" onClick={() => onBabies(l)} data-testid={`litter-babies-${l.id}`} className="rounded-lg h-8 gap-1"><Baby size={13} /> {t("bb_btn")}</Button>
          <Button size="sm" variant="outline" onClick={() => onUpdates(l)} data-testid={`litter-updates-${l.id}`} className="rounded-lg h-8 gap-1"><Images size={13} /> {t("lu_btn")}</Button>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!l.waitlist_count} data-testid={`litter-export-${l.id}`} className="rounded-lg h-8 gap-1"><Download size={13} /> CSV</Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(l)} data-testid={`litter-edit-${l.id}`} className="rounded-lg h-8 gap-1"><Pencil size={13} /> {t("bl_edit")}</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`litter-delete-${l.id}`} className="rounded-lg h-8 text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={13} /></Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-white">
              <AlertDialogHeader><AlertDialogTitle>{t("bl_del_title")}</AlertDialogTitle><AlertDialogDescription>{t("bl_del_desc")} {l.waitlist_count} {t("bl_on_wl")}.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>{t("lt_cancel")}</AlertDialogCancel><AlertDialogAction onClick={del} data-testid={`litter-delete-confirm-${l.id}`} className="bg-red-500 hover:bg-red-600">{t("bl_delete")}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <WaitlistTable litter={l} onChanged={onReload} />
    </div>
  );
};

export function BreederLitters() {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const [messaging, setMessaging] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [babies, setBabies] = useState(null);
  const [slots, setSlots] = useState(null);
  const [sharing, setSharing] = useState(null);
  const load = () => api.get("/my/litters").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="breeder-litters-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold text-[#12234A]">{t("lt_tab")}</h3>
          <p className="text-xs text-slate-500">{t("bl_sub")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`${API}/litter-journey/guide.pdf?lang=${lang === "fr" ? "fr" : "en"}`} target="_blank" rel="noreferrer" data-testid="litter-guide-pdf-btn"
            className="h-10 rounded-xl border border-slate-200 bg-white hover:border-[#F5551E] hover:text-[#F5551E] text-[#12234A] text-sm font-semibold px-4 inline-flex items-center gap-1.5 transition-colors">
            <FileText size={15} /> {lang === "fr" ? "Guide acheteur (PDF)" : "Buyer guide (PDF)"}</a>
          <JourneyShareButton fr={lang === "fr"} testid="litter-journey-share-btn" />
          <Button onClick={() => setEditing(null)} data-testid="litter-new-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5"><Plus size={16} /> {t("bl_announce")}</Button>
        </div>
      </div>
      {items === null ? <Loader /> : items.length === 0 ? (
        <Empty icon={Egg} text={t("bl_empty")} cta={t("bl_announce")} onCta={() => setEditing(null)} />
      ) : (
        <div className="space-y-3">{items.map((l) => <LitterCard key={l.id} l={l} onEdit={setEditing} onReload={load} onMessage={setMessaging} onUpdates={setUpdating} onBabies={setBabies} onSlots={setSlots} onShare={setSharing} />)}</div>
      )}
      <WaitlistMessageDialog litter={messaging} onClose={() => setMessaging(null)} />
      <LitterUpdatesManager litter={updating} onClose={() => setUpdating(null)} />
      <BabiesManager litter={babies} onClose={() => setBabies(null)} />
      <SlotsManager litter={slots} onClose={() => setSlots(null)} />
      <LitterShareDialog litter={sharing} onClose={() => setSharing(null)} />
      <LitterFormDialog open={editing !== undefined} litter={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
    </div>
  );
}
